import React, { useEffect, useState } from "react";
import {
  MDBBadge,
  MDBBtn,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBSpinner,
  MDBTextArea,
} from "mdb-react-ui-kit";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import {
  addIncidentMessage,
  assignIncident,
  getIncidentDetail,
  resolveIncidentByUser,
  updateIncidentStatus,
} from "Projects/CC_cubi_clan_project/Utils/IncidentService";
import { mfmThumbUrl, mfmPosterUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import { MfmImageUploader } from "Projects/CC_cubi_clan_project/GlobalComponents/MfmImageUploader/MfmImageUploader";
import "./IncidentDetailModal.css";

const STATUS_LABEL = {
  OPEN: "Abierta",
  IN_PROGRESS: "En curso",
  WAITING_USER: "Esperando usuario",
  RESOLVED: "Resuelta",
  CLOSED: "Cerrada",
};

const PRIORITY_COLOR = {
  LOW: "success",
  MEDIUM: "warning",
  HIGH: "danger",
  URGENT: "dark",
};

function parseJsonArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * IncidentDetailModal
 *
 * Props:
 *   show          {bool}     Visibilidad del modal
 *   onClose       {func}     Callback de cierre
 *   incidentId    {number}   ID de la incidencia a cargar (null = no cargar)
 *   isManager     {bool}     Si el usuario tiene rol de gestor
 *   onUpdated     {func}     Callback llamado tras cualquier actualización (estado, mensaje, asignación)
 */
export default function IncidentDetailModal({ show, onClose, incidentId, isManager, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [internalNote, setInternalNote] = useState(false);
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [statusDraft, setStatusDraft] = useState("OPEN");
  const [assignedDraft, setAssignedDraft] = useState("");

  useEffect(() => {
    if (show && incidentId != null) {
      loadDetail(incidentId);
    } else {
      setDetail(null);
      setNewMessage("");
      setInternalNote(false);
      setMessageAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, incidentId]);

  const loadDetail = async (id) => {
    setLoading(true);
    try {
      const data = await getIncidentDetail(id);
      setDetail(data);
      if (data?.estado) setStatusDraft(data.estado);
      setAssignedDraft(data?.idUsuarioAsignado != null ? String(data.idUsuarioAsignado) : "");
    } catch {
      toast.error("No se pudo cargar el detalle de la incidencia");
    } finally {
      setLoading(false);
    }
  };

  const onMediaPreviewError = (event) => {
    const img = event.currentTarget;
    const uuid = img?.dataset?.uuid;
    if (!uuid) return;
    if (!img.dataset.triedPoster) {
      img.dataset.triedPoster = "1";
      img.src = mfmPosterUrl(uuid);
      return;
    }
    img.onerror = null;
  };

  const addAttachment = (result) => {
    const uuid = result?.uuid;
    if (!uuid) return;
    setMessageAttachments((prev) => (prev.includes(uuid) ? prev : [...prev, uuid]));
  };

  const sendMessage = async () => {
    if (!incidentId || (!newMessage.trim() && messageAttachments.length === 0)) return;
    setLoading(true);
    try {
      const payload = {
        content: newMessage.trim(),
        internalNote,
        messageType: internalNote ? "NOTE" : "MESSAGE",
        attachmentsJson: messageAttachments.length ? JSON.stringify(messageAttachments) : null,
      };
      const res = await addIncidentMessage(incidentId, payload);
      if (res.ok && res.json?.STATE === "SUCCESS") {
        setNewMessage("");
        setInternalNote(false);
        setMessageAttachments([]);
        await loadDetail(incidentId);
        onUpdated?.();
      } else {
        toast.error(res.json?.MESSAGE || "No se pudo enviar el mensaje");
      }
    } finally {
      setLoading(false);
    }
  };

  const markResolved = async () => {
    if (!incidentId) return;
    setLoading(true);
    try {
      const res = await resolveIncidentByUser(incidentId);
      if (res.ok && res.json?.STATE === "SUCCESS") {
        await loadDetail(incidentId);
        onUpdated?.();
      } else {
        toast.error(res.json?.MESSAGE || "No se pudo marcar como resuelta");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveManagerChanges = async () => {
    if (!incidentId || !isManager) return;
    setLoading(true);
    try {
      const updates = [];
      if (statusDraft && statusDraft !== detail?.estado) {
        updates.push(updateIncidentStatus(incidentId, statusDraft));
      }
      const parsedAssigned = assignedDraft.trim() === "" ? null : Number(assignedDraft);
      const currentAssigned = detail?.idUsuarioAsignado == null ? null : Number(detail.idUsuarioAsignado);
      if (parsedAssigned !== currentAssigned && (parsedAssigned == null || Number.isFinite(parsedAssigned))) {
        updates.push(assignIncident(incidentId, parsedAssigned));
      }
      if (!updates.length) {
        toast.info("No hay cambios de gestión pendientes");
        return;
      }
      const results = await Promise.all(updates);
      const failed = results.find((r) => !(r.ok && r.json?.STATE === "SUCCESS"));
      if (failed) {
        toast.error(failed.json?.MESSAGE || "No se pudieron guardar los cambios");
      } else {
        await loadDetail(incidentId);
        onUpdated?.();
      }
    } finally {
      setLoading(false);
    }
  };

  const mediaFromDescription = parseJsonArray(detail?.descriptionMediaJson);

  return (
    <MDBModal show={show} onHide={onClose} tabIndex="-1" className="inci-detail-modal">
      <MDBModalDialog size="xl" scrollable>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>
              {detail
                ? <>
                    <span className="text-muted me-2">#{detail.idIncidencia}</span>
                    {detail.titulo}
                    <MDBBadge color={PRIORITY_COLOR[detail.prioridad] || "secondary"} className="ms-2">
                      {detail.prioridad}
                    </MDBBadge>
                    <MDBBadge color="info" className="ms-2">
                      {STATUS_LABEL[detail.estado] || detail.estado}
                    </MDBBadge>
                  </>
                : "Cargando incidencia…"
              }
            </MDBModalTitle>
            <MDBBtn className="btn-close" color="none" onClick={onClose} />
          </MDBModalHeader>

          <MDBModalBody>
            {loading && (
              <div className="inci-modal-loading">
                <MDBSpinner />
              </div>
            )}

            {!loading && detail && (
              <>
                {/* Metadatos */}
                <div className="inci-modal-meta mb-3">
                  <span className="text-muted small me-3">
                    <MDBIcon fas icon="tag" className="me-1" />{detail.appCode}
                  </span>
                  <span className="text-muted small me-3">
                    <MDBIcon fas icon="user" className="me-1" />
                    {detail.creadorApodo || detail.creadorNombre || "Usuario"}
                  </span>
                  {detail.asignadoApodo && (
                    <span className="text-muted small me-3">
                      <MDBIcon fas icon="user-check" className="me-1" />Asignada a: {detail.asignadoApodo}
                    </span>
                  )}
                  {detail.categoria && (
                    <span className="text-muted small">
                      <MDBIcon fas icon="folder" className="me-1" />{detail.categoria}
                    </span>
                  )}
                </div>

                {/* Descripción */}
                <div className="inci-modal-desc mb-3">
                  <p>{detail.descripcion || "Sin descripción"}</p>
                  {!!mediaFromDescription.length && (
                    <div className="inci-media-preview">
                      {mediaFromDescription.map((uuid) => (
                        <a href={mfmThumbUrl(uuid)} target="_blank" rel="noreferrer" key={uuid}>
                          <img src={mfmThumbUrl(uuid)} alt={uuid} data-uuid={uuid} onError={onMediaPreviewError} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bloque de gestión (solo manager) */}
                {isManager && (
                  <div className="inci-manager-box mb-3">
                    <h6 className="mb-2">
                      <MDBIcon fas icon="tools" className="me-2" />Gestión
                    </h6>
                    <div className="d-flex flex-wrap gap-2 align-items-end">
                      <div>
                        <label className="form-label small">Estado</label>
                        <select
                          className="form-select"
                          value={statusDraft}
                          onChange={(e) => setStatusDraft(e.target.value)}
                        >
                          <option value="OPEN">OPEN</option>
                          <option value="IN_PROGRESS">IN_PROGRESS</option>
                          <option value="WAITING_USER">WAITING_USER</option>
                          <option value="RESOLVED">RESOLVED</option>
                          <option value="CLOSED">CLOSED</option>
                        </select>
                      </div>
                      <MDBInput
                        label="Asignar a ID usuario"
                        value={assignedDraft}
                        onChange={(e) => setAssignedDraft(e.target.value)}
                      />
                      <MDBBtn size="sm" onClick={saveManagerChanges}>
                        <MDBIcon fas icon="save" className="me-1" />Guardar gestión
                      </MDBBtn>
                    </div>
                  </div>
                )}

                {/* Botón resolver (creador) */}
                {detail.isOwner && detail.estado !== "RESOLVED" && detail.estado !== "CLOSED" && (
                  <div className="mb-3">
                    <MDBBtn size="sm" color="success" outline onClick={markResolved}>
                      <MDBIcon fas icon="check-circle" className="me-1" />Marcar como resuelta
                    </MDBBtn>
                  </div>
                )}

                <hr />

                {/* Mensajes */}
                <h6 className="mb-2">
                  <MDBIcon fas icon="comments" className="me-2" />Mensajes
                </h6>
                <div className="inci-messages mb-3">
                  {(detail.mensajes || []).map((msg) => {
                    const attachments = parseJsonArray(msg.attachmentsJson);
                    return (
                      <div key={msg.idMensaje} className={`inci-msg ${msg.esNotaInterna ? "inci-msg-note" : ""}`}>
                        <div className="d-flex justify-content-between">
                          <strong>{msg.autorApodo || msg.autorNombre || "Usuario"}</strong>
                          <small className="text-muted">{msg.fechaCreacion}</small>
                        </div>
                        {msg.esNotaInterna && <MDBBadge color="dark" className="mb-1">Nota interna</MDBBadge>}
                        <div>{msg.contenido || ""}</div>
                        {!!attachments.length && (
                          <div className="inci-media-preview mt-2">
                            {attachments.map((uuid) => (
                              <a href={mfmThumbUrl(uuid)} target="_blank" rel="noreferrer" key={uuid}>
                                <img src={mfmThumbUrl(uuid)} alt={uuid} data-uuid={uuid} onError={onMediaPreviewError} />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!(detail.mensajes || []).length && (
                    <div className="text-muted small">Sin mensajes todavía.</div>
                  )}
                </div>

                {/* Formulario de respuesta */}
                <div>
                  <MDBTextArea
                    rows={3}
                    label="Responder"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                  />
                  {isManager && (
                    <div className="form-check mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="inci-modal-internal-note"
                        checked={internalNote}
                        onChange={(e) => setInternalNote(e.target.checked)}
                      />
                      <label htmlFor="inci-modal-internal-note" className="form-check-label">
                        Nota interna (solo gestores)
                      </label>
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-2 mt-2 mb-2">
                    <MfmImageUploader appCode="MFM" library="incidencias" mediaType="image" onUpload={addAttachment} />
                    <MfmImageUploader appCode="MFM" library="incidencias" mediaType="video" onUpload={addAttachment} />
                  </div>
                  {!!messageAttachments.length && (
                    <div className="inci-attachment-list mb-2">
                      {messageAttachments.map((uuid) => (
                        <button
                          key={uuid}
                          className="inci-attachment-pill"
                          onClick={() => setMessageAttachments((prev) => prev.filter((it) => it !== uuid))}
                        >
                          {uuid}
                          <MDBIcon fas icon="times" className="ms-2" />
                        </button>
                      ))}
                    </div>
                  )}
                  <MDBBtn onClick={sendMessage} disabled={loading}>
                    <MDBIcon fas icon="paper-plane" className="me-2" />Enviar mensaje
                  </MDBBtn>
                </div>
              </>
            )}
          </MDBModalBody>

          <MDBModalFooter>
            <MDBBtn color="secondary" onClick={onClose}>Cerrar</MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}
